import { ContextualText } from "~/components/contextual-text";
import { AccentRow } from "~/components/patterns/accent-row";
import {
  getHelpGroupAnchorId,
  type ResolvedHelpGroup,
} from "~/config/help-center-groups";
import { type SearchableArticle } from "~/lib/help-center-search";
import { SupportFooterCta } from "./support-footer-cta";

/**
 * Renders the same group-section style as `ProspectLanding`, without task
 * shortcuts or FAQ: `/klijenti` gets the six client groups, `/terapeuti` the
 * four therapist ones, both through `resolveHelpGroups`. The group nav lives
 * in the header's `afterTitle` (see `help-center-client.tsx`), which needs
 * these same resolved groups to build matching anchors.
 */
export function AudienceCatalog({
  groups,
}: {
  groups: ResolvedHelpGroup<SearchableArticle>[];
}) {
  return (
    <div>
      <div className="divide-y divide-border">
        {groups.map((group, index) => {
          const anchorId = getHelpGroupAnchorId(group.definition);

          return (
            <section
              key={anchorId}
              id={anchorId}
              aria-labelledby={`${anchorId}-heading`}
              className={`scroll-mt-24 ${index % 2 === 0 ? "bg-secondary" : "bg-background"}`}
            >
              <div className="max-w-8xl container mx-auto px-4 py-10 md:px-8 md:py-14">
                <div className="mb-7 max-w-2xl md:mb-9">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${group.accent.badge}`}
                  >
                    Pregledaj
                  </span>
                  <h2
                    id={`${anchorId}-heading`}
                    className="marketing-heading mt-3 text-2xl text-foreground sm:text-3xl"
                  >
                    {group.definition.title}
                  </h2>
                  <p className="mt-2 text-muted-foreground">
                    <ContextualText
                      text={group.definition.description}
                      links={
                        group.definition.audience === "client" &&
                        group.definition.category === "Cene i plaćanje"
                          ? [
                              {
                                text: "Koliko košta terapija",
                                href: "/pristupacna-cena-psihoterapije",
                              },
                            ]
                          : []
                      }
                    />
                  </p>
                </div>
                <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {group.articles.map((article) => (
                    <AccentRow
                      key={article.slug}
                      href={article.slug}
                      icon={group.definition.icon}
                      title={article.title}
                      description={article.description}
                      accent={group.accent}
                    />
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <section className="border-t bg-background">
        <div className="max-w-8xl container mx-auto px-4 py-10 md:px-8 md:py-14">
          <div className="max-w-2xl">
            <h2 className="marketing-heading text-2xl text-foreground sm:text-3xl">
              Nisi pronašao odgovor?
            </h2>
            <div className="mt-6">
              <SupportFooterCta />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
